// frontend/src/pages/Contact.jsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, User, MessageSquare, Send, Phone, MapPin, Clock, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { API_BASE_URL } from "../config/api";
import Swal from "sweetalert2";

export default function Contact() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    telefone: "",
    assunto: "",
    mensagem: "",
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.nome.trim()) newErrors.nome = "Nome é obrigatório";
    if (!formData.email.trim()) {
      newErrors.email = "E-mail é obrigatório";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "E-mail inválido";
    }
    if (!formData.mensagem.trim()) {
      newErrors.mensagem = "Mensagem é obrigatória";
    } else if (formData.mensagem.trim().length < 10) {
      newErrors.mensagem = "Mensagem deve ter pelo menos 10 caracteres";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/contact`, formData);
      
      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "✅ Mensagem enviada!",
          text: "Sua mensagem foi enviada com sucesso. Entraremos em contato em breve.",
          confirmButtonColor: "#449879",
          confirmButtonText: "OK",
          customClass: {
            popup: "rounded-2xl",
            confirmButton: "rounded-xl px-6 py-2.5",
          },
        }).then(() => {
          setFormData({
            nome: "",
            email: "",
            telefone: "",
            assunto: "",
            mensagem: "",
          });
          navigate("/");
        });
      }
    } catch (error) {
      console.error("Error sending message:", error);
      Swal.fire({
        icon: "error",
        title: "Erro!",
        text: error.response?.data?.message || "Não foi possível enviar a mensagem. Tente novamente.",
        confirmButtonColor: "#449879",
        confirmButtonText: "OK",
        customClass: {
          popup: "rounded-2xl",
          confirmButton: "rounded-xl px-6 py-2.5",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-2 pb-16 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[#449779]/10 px-4 py-2 rounded-full mb-4">
            <Mail className="w-4 h-4 text-[#449779]" />
            <span className="text-xs font-medium text-[#449779]">Fale Conosco</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">
            Entre em contato
          </h1>
          <p className="text-gray-500 max-w-lg mx-auto text-sm">
            Tem alguma dúvida, sugestão ou precisa de ajuda? Envie uma mensagem para nossa equipe.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* ===== INFO DE CONTACTO ===== */}
          <div className="md:col-span-1 space-y-4">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="font-semibold text-gray-800 mb-4 text-lg">
                📬 Informações
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#449779]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4 text-[#449779]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Email</p>
                    <a href="mailto:info@brechovizinho.com.br" className="text-sm text-[#449779] font-medium hover:underline break-all">
                      info@brechovizinho.com.br
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#449779]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4 text-[#449779]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Telefone</p>
                    <p className="text-sm text-gray-700">(11) 91864-4348</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#449779]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-4 h-4 text-[#449779]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Atendimento</p>
                    <p className="text-sm text-gray-700">Segunda a Sexta</p>
                    <p className="text-xs text-gray-400">09:00 - 18:00</p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Sugerido */}
            <div className="bg-[#449779]/5 rounded-2xl p-5 border border-[#449779]/10">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                💡 Dúvidas frequentes?
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                Confira nossa página de perguntas frequentes antes de enviar uma mensagem.
              </p>
              <button
                onClick={() => navigate("/faq")}
                className="text-xs text-[#449779] font-medium hover:underline flex items-center gap-1"
              >
                Ver FAQs →
              </button>
            </div>
          </div>

          {/* ===== FORMULARIO ===== */}
          <div className="md:col-span-2">
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
              <h2 className="font-semibold text-gray-800 mb-6 text-lg">
                ✉️ Envie sua mensagem
              </h2>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Nombre */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome completo <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      placeholder="Seu nome completo"
                      className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449779] focus:border-transparent ${
                        errors.nome ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                  </div>
                  {errors.nome && (
                    <p className="text-red-500 text-xs mt-1">{errors.nome}</p>
                  )}
                </div>

                {/* Email y Teléfono */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      E-mail <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="seu@email.com"
                        className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449779] focus:border-transparent ${
                          errors.email ? "border-red-500" : "border-gray-300"
                        }`}
                      />
                    </div>
                    {errors.email && (
                      <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefone <span className="text-gray-400 text-xs">(opcional)</span>
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="tel"
                        name="telefone"
                        value={formData.telefone}
                        onChange={handleChange}
                        placeholder="(11) 99999-9999"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449779] focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Asunto */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assunto <span className="text-gray-400 text-xs">(opcional)</span>
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      name="assunto"
                      value={formData.assunto}
                      onChange={handleChange}
                      placeholder="Ex: Dúvida sobre anúncio"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449779] focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Mensaje */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mensagem <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="mensagem"
                    value={formData.mensagem}
                    onChange={handleChange}
                    rows={5}
                    placeholder="Descreva sua dúvida, sugestão ou problema..."
                    className={`w-full px-4 py-3 border rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#449779] focus:border-transparent ${
                      errors.mensagem ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.mensagem && (
                    <p className="text-red-500 text-xs mt-1">{errors.mensagem}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    Mínimo 10 caracteres
                  </p>
                </div>

                {/* Botón */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#449779] text-white py-3.5 rounded-xl font-semibold hover:bg-[#367d64] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.01] active:scale-[0.98]"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Enviar mensagem
                    </>
                  )}
                </button>

                <p className="text-[10px] text-gray-400 text-center mt-2">
                  🔒 Sua mensagem será enviada de forma segura para nossa equipe.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}